<template>
  <div class="archive-view">
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ t('archive.title') }}</h1>
        <p class="page-sub">{{ t('archive.sub') }}</p>
      </div>
    </div>

    <div class="arch-tabs">
      <button class="tab-btn" :class="{ active: tab === 'tasks' }" @click="tab = 'tasks'; loadTasks()">{{ t('archive.tasks') }}</button>
      <button class="tab-btn" :class="{ active: tab === 'projects' }" @click="tab = 'projects'; loadProjects()">{{ t('archive.projects') }}</button>
    </div>

    <!-- ── Archived Tasks ── -->
    <template v-if="tab === 'tasks'">
      <div v-if="tasksLoading" class="spinner"></div>
      <div v-else-if="tasks.length === 0" class="empty-state">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" opacity=".3"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
        <p>{{ t('archive.noTasks') }}</p>
      </div>
      <div v-else class="arch-list">
        <div v-for="row in tasks" :key="row.id" class="arch-item">
          <div class="arch-stripe" :style="{ background: urgencyColor(row.urgency) }"></div>
          <div class="arch-body">
            <div class="arch-title">{{ row.title }}</div>
            <div class="arch-meta">
              <span v-if="row.project_name" class="arch-tag" :style="{ color: row.project_color }">{{ row.project_name }}</span>
              <span class="arch-badge" :class="'badge-' + row.urgency">{{ t('urgency.' + row.urgency) }}</span>
              <span class="arch-badge" :class="'badge-' + row.status">{{ t('status.' + row.status) }}</span>
              <span v-if="row.archived_at" class="arch-date">{{ t('archive.archivedOn') }} {{ formatDate(row.archived_at) }}</span>
            </div>
          </div>
          <button class="btn btn-sm btn-ghost arch-restore" @click="restoreTask(row)">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/></svg>
            {{ t('archive.restore') }}
          </button>
        </div>
      </div>
    </template>

    <!-- ── Archived Projects ── -->
    <template v-if="tab === 'projects'">
      <div v-if="projsLoading" class="spinner"></div>
      <div v-else-if="projs.length === 0" class="empty-state">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" opacity=".3"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
        <p>{{ t('archive.noProjects') }}</p>
      </div>
      <div v-else class="arch-list">
        <div v-for="p in projs" :key="p.id" class="arch-item">
          <div class="arch-stripe" :style="{ background: p.color }"></div>
          <div class="arch-body">
            <div class="arch-title">{{ p.name }}</div>
            <div class="arch-meta">
              <span v-if="p.description" class="arch-desc">{{ p.description }}</span>
              <span class="arch-date">{{ t('archive.archivedOn') }} {{ formatDate(p.archived_at) }}</span>
            </div>
          </div>
          <button class="btn btn-sm btn-ghost arch-restore" @click="restoreProject(p)">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/></svg>
            {{ t('archive.restore') }}
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import api from '@/api'
import { useI18n } from '@/composables/useI18n'

const URGENCY_COLORS = { low: '#A8A192', normal: '#0F6E59', high: '#A66E08', critical: '#B8432B' }

export default {
  name: 'ArchiveView',
  setup() {
    const { t, locale } = useI18n()
    const tab         = ref('tasks')
    const tasks       = ref([])
    const projs       = ref([])
    const tasksLoading = ref(true)
    const projsLoading = ref(false)

    async function loadTasks() {
      tasksLoading.value = true
      try { tasks.value = (await api.getTasks({ archived: 1 })).data || [] } catch {} finally { tasksLoading.value = false }
    }

    async function loadProjects() {
      projsLoading.value = true
      try { projs.value = (await api.getProjects({ archived: 1 })).data || [] } catch {} finally { projsLoading.value = false }
    }

    async function restoreTask(row) {
      if (!confirm(t('archive.restoreConfirm', { title: row.title }))) return
      try {
        await api.restoreTask(row.id)
        tasks.value = tasks.value.filter(x => x.id !== row.id)
      } catch {}
    }

    async function restoreProject(p) {
      if (!confirm(t('archive.restoreConfirm', { title: p.name }))) return
      try {
        await api.restoreProject(p.id)
        projs.value = projs.value.filter(x => x.id !== p.id)
      } catch {}
    }

    const urgencyColor = (u) => URGENCY_COLORS[u] || '#76B900'

    function formatDate(dt) {
      if (!dt) return ''
      const loc = locale.value === 'he' ? 'he-IL' : 'en-GB'
      return new Date(dt).toLocaleDateString(loc, { day: '2-digit', month: 'short', year: 'numeric' })
    }

    onMounted(loadTasks)
    return { t, tab, tasks, projs, tasksLoading, projsLoading, loadTasks, loadProjects, urgencyColor, formatDate, restoreTask, restoreProject }
  },
}
</script>

<style scoped>
.page-sub { color: var(--muted); font-size: 13px; margin-top: 2px; }

.arch-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 24px;
}
.tab-btn {
  padding: 6px 18px;
  border-radius: 20px;
  border: 1px solid var(--border);
  background: none;
  color: var(--muted);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all .15s;
}
.tab-btn:hover  { border-color: var(--primary); color: var(--primary); }
.tab-btn.active { background: var(--primary); border-color: var(--primary); color: #fff; }

.arch-list { display: flex; flex-direction: column; gap: 8px; }

.arch-item {
  display: flex;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow: hidden;
  opacity: .75;
}
.arch-stripe { width: 4px; flex-shrink: 0; }
.arch-body { flex: 1; padding: 12px 16px; }
.arch-restore { align-self: center; margin: 0 12px; flex-shrink: 0; }
.arch-title { font-size: 14px; font-weight: 600; margin-bottom: 6px; }
.arch-meta  { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.arch-tag   { font-size: 11px; font-weight: 600; }
.arch-desc  { font-size: 12px; color: var(--muted); }
.arch-date  { font-size: 11px; color: var(--muted); margin-left: auto; }
.arch-badge {
  font-size: 11px;
  padding: 1px 7px;
  border-radius: 5px;
  font-weight: 500;
  background: var(--badge-bg);
  border: 1px solid var(--border-2);
  color: var(--muted);
}
</style>
